import React from "react";
import { Link } from "react-router-dom";
import { Scale, ChevronLeft } from "lucide-react";

/**
 * Terms of Service — public. Covers acceptable use, club / member
 * responsibilities, and the privacy commitments inherited from
 * /privacy. Members tick a "I agree" box in onboarding.
 */
export default function TermsOfServicePage() {
  return (
    <div className="bg-cream min-h-screen" data-testid="terms-page">
      <header className="bg-[#0f1a14] text-[#F9F8F4] border-b border-[#1A422D]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Scale size={22} className="text-[#D9C68A]" />
            <div>
              <div className="overline" style={{ color: "#D9C68A" }}>My Crest Club</div>
              <div className="font-serif text-2xl mt-0.5">Terms of Service</div>
            </div>
          </div>
          <Link
            to="/login"
            data-testid="terms-back"
            className="inline-flex items-center gap-2 border border-[#D9C68A]/50 text-[#D9C68A] px-3 py-1.5 text-xs uppercase tracking-[0.22em] hover:bg-[#1A422D] transition-colors"
          >
            <ChevronLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10 space-y-10">
        <p className="text-sm text-[#7A7A7A]">Effective February 1, 2026</p>

        <section data-testid="terms-agreement" className="space-y-4">
          <h2 className="font-serif text-3xl text-[#1A1A1A]">Your agreement</h2>
          <p className="text-[#1A1A1A] leading-relaxed">
            By creating an account or signing in to My Crest Club, you accept
            these Terms and the accompanying{" "}
            <Link className="underline text-[#1A422D]" to="/privacy" data-testid="terms-link-privacy">Privacy Policy</Link>.
            Your club&rsquo;s relationship with My Crest Club is governed by a
            separate operator agreement signed by the club&rsquo;s authorized
            administrator.
          </p>
        </section>

        <section data-testid="terms-acceptable-use" className="space-y-4">
          <h3 className="font-serif text-2xl text-[#1A1A1A]">Acceptable use</h3>
          <ul className="space-y-3 text-[#1A1A1A] leading-relaxed list-disc pl-5">
            <li>Use My Crest Club only to submit and track special orders with your own club&rsquo;s pro shop.</li>
            <li>Do not attempt to bypass tenant isolation, scrape data outside your own club, or impersonate another member or staff member.</li>
            <li>Photos you upload must be your own or properly licensed; pro-shop staff may decline any image at their discretion.</li>
            <li>Reverse engineering, automated scraping, and brute-force credential testing are prohibited and detected.</li>
          </ul>
        </section>

        <section data-testid="terms-data" className="space-y-4">
          <h3 className="font-serif text-2xl text-[#1A1A1A]">Your data, your club&rsquo;s data</h3>
          <p className="text-[#1A1A1A] leading-relaxed">
            Profile data and special-order records you create are owned by you
            and your club. We hold them in trust under the safeguards
            described in the{" "}
            <Link className="underline text-[#1A422D]" to="/privacy">Privacy Policy</Link>.
            Your club may export, archive, or delete records in accordance
            with their own retention policies. My Crest Club retains forensic
            snapshots of destructive operations for 90 days so accidental
            deletions can be recovered.
          </p>
        </section>

        <section data-testid="terms-availability" className="space-y-4">
          <h3 className="font-serif text-2xl text-[#1A1A1A]">Availability and changes</h3>
          <p className="text-[#1A1A1A] leading-relaxed">
            We make every reasonable effort to keep the platform available and
            responsive, but we don&rsquo;t promise uninterrupted service. We may
            update these Terms; we&rsquo;ll notify the email on file if anything
            material changes. Continued use after the effective date is your
            acceptance.
          </p>
        </section>

        <section data-testid="terms-termination" className="space-y-4">
          <h3 className="font-serif text-2xl text-[#1A1A1A]">Termination</h3>
          <p className="text-[#1A1A1A] leading-relaxed">
            You may close your account at any time by contacting your club&rsquo;s
            pro shop. Your club may remove you from its roster at its sole
            discretion (your account will then be deleted if you have no other
            active memberships). My Crest Club may suspend an account that
            violates these Terms.
          </p>
        </section>

        <section data-testid="terms-liability" className="space-y-4">
          <h3 className="font-serif text-2xl text-[#1A1A1A]">Limitation of liability</h3>
          <p className="text-[#1A1A1A] leading-relaxed">
            My Crest Club is provided &ldquo;as is&rdquo;. To the maximum extent
            permitted by law, our aggregate liability to any user is limited
            to fees paid (if any) in the prior 12 months.
          </p>
        </section>

        <section data-testid="terms-contact" className="border-t border-[#E5E3D8] pt-8 space-y-2">
          <h3 className="font-serif text-2xl text-[#1A1A1A]">Questions</h3>
          <p className="text-[#1A1A1A] leading-relaxed">
            Email <a className="underline text-[#1A422D]" href="mailto:theshop@mycrestclub.com">theshop@mycrestclub.com</a>.
          </p>
        </section>
      </main>

      <footer className="max-w-3xl mx-auto px-6 sm:px-10 py-6 text-xs text-[#7A7A7A] flex flex-wrap gap-4">
        <Link to="/login" className="hover:text-[#1A422D]">Sign in</Link>
        <Link to="/privacy" className="hover:text-[#1A422D]" data-testid="footer-privacy">Privacy Policy</Link>
        <span>&copy; My Crest Club</span>
      </footer>
    </div>
  );
}
